import React, { memo } from 'react';
import { Handle, getBezierPath, getEdgeCenter } from 'react-flow-renderer';
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import Tooltip from "@material-ui/core/Tooltip";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import ReceiptIcon from "@material-ui/icons/Receipt";
import RedoIcon from "@material-ui/icons/Redo";
import AccountTreeIcon from "@material-ui/icons/AccountTree";

const useStyles = makeStyles((theme) => ({
    node: {
        padding: "10px",
        borderRadius: "5px",
        background: "#fff",
        border: "1px solid #777",
        minWidth: "150px",
        textAlign: "center",
        position: "relative",
    },
    header: {
        fontWeight: "bold",
        marginBottom: "5px",
        borderBottom: "1px solid #eee",
        paddingBottom: "5px"
    },
    content: {
        fontSize: "12px"
    },
    cloneButton: {
        position: "absolute",
        top: "-8px",
        right: "-8px",
        padding: "4px",
        background: "#fff",
        border: "1px solid #ddd",
        "&:hover": {
            background: "#f0f0f0",
        }
    },
    cloneIcon: {
        fontSize: "14px"
    },
    edgeButton: {
        width: "20px",
        height: "20px",
        background: "#eee",
        border: "1px solid #777",
        cursor: "pointer",
        borderRadius: "50%",
        fontSize: "12px",
        lineHeight: "1",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover": {
            boxShadow: "0 0 6px 2px rgba(0, 0, 0, 0.08)",
            background: "#fff",
        },
    },
}));

export const TextNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header}>Text Message</div>
            <div className={classes.content}>
                {data.label || "Click to edit"}
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const ButtonNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#007bff", minWidth: "200px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#007bff" }}>Buttons</div>
            <div className={classes.content}>
                <div style={{ marginBottom: 10 }}>{data.label || "Click to edit"}</div>
                {data.buttons && data.buttons.map((btn, index) => (
                    <div key={index} style={{
                        background: "#eee",
                        padding: "5px",
                        margin: "5px 0",
                        borderRadius: "3px",
                        position: "relative",
                        textAlign: "left"
                    }}>
                        {btn.label}
                        <Handle
                            type="source"
                            position="right"
                            id={btn.id}
                            style={{ top: "50%", background: "#555" }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
});

export const ListNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#3f51b5", minWidth: "200px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#3f51b5" }}>Lista</div>
            <div className={classes.content}>
                <div style={{ marginBottom: 10 }}>{data.label || "Click to edit"}</div>
                {data.listItems && data.listItems.map((item, index) => (
                    <div key={index} style={{
                        background: "#e8eaf6",
                        padding: "5px",
                        margin: "5px 0",
                        borderRadius: "3px",
                        position: "relative",
                        textAlign: "left",
                        borderLeft: "3px solid #3f51b5"
                    }}>
                        <div style={{ fontSize: "11px", fontWeight: "bold" }}>{item.title}</div>
                        {item.description && (
                            <div style={{ fontSize: "9px", color: "#666", marginTop: 2 }}>{item.description}</div>
                        )}
                        <Handle
                            type="source"
                            position="right"
                            id={item.id}
                            style={{ top: "50%", background: "#3f51b5" }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
});

export const ImageNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#e83e8c" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#e83e8c" }}>Image</div>
            <div className={classes.content}>
                {data.url ? (
                    <img src={data.url} alt="Media" style={{ maxWidth: "100%", maxHeight: "100px" }} />
                ) : (
                    <div>No Image Selected</div>
                )}
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const InputNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#ffc107" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#ffc107" }}>Input / Pergunta</div>
            <div className={classes.content}>
                {data.label || "Click to edit"}
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const ResolveNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#dc3545" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#dc3545" }}>Finalizar Atendimento</div>
            <div className={classes.content}>
                Encerra o ticket automaticamente.
            </div>
        </div>
    );
});

export const SpecialtiesNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#6f42c1" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#6f42c1" }}>Specialties (Auto)</div>
            <div className={classes.content}>
                {data.label || "Click to edit title"}
                <div style={{ fontStyle: "italic", marginTop: 5, color: "#888" }}>
                    (Dynamically fetches specialties)
                </div>
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const ServicesNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#e83e8c" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#e83e8c" }}>Services (Auto)</div>
            <div className={classes.content}>
                {data.label || "Click to edit title"}
                <div style={{ fontStyle: "italic", marginTop: 5, color: "#888" }}>
                    (Fetches SubSpecialties)
                </div>
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const ProfessionalsNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#28a745" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#28a745" }}>Professionals (Auto)</div>
            <div className={classes.content}>
                {data.label || "Click to edit title"}
                <div style={{ fontStyle: "italic", marginTop: 5, color: "#888" }}>
                    (Fetches Professionals by Service)
                </div>
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});



export const DateSelectionNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#17a2b8" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#17a2b8" }}>Data (Auto)</div>
            <div className={classes.content}>
                {data.label || "Selecione uma data:"}
                <div style={{ fontStyle: "italic", marginTop: 5, color: "#888" }}>
                    (Next 3 days + "Outra Data")
                </div>
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const HoursSelectionNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#6610f2" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#6610f2" }}>Horários (Auto)</div>
            <div className={classes.content}>
                {data.label || "Selecione um horário:"}
                <div style={{ fontStyle: "italic", marginTop: 5, color: "#888" }}>
                    (Fetches availability)
                </div>
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const ConfirmationNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#28a745", minWidth: "200px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#28a745" }}>Confirmação (Auto)</div>
            <div className={classes.content}>
                <div style={{ marginBottom: 10 }}>{data.label || "Você quer agendar um horário para:"}</div>
                <div style={{ fontStyle: "italic", fontSize: "10px", color: "#888", marginBottom: 10 }}>
                    (Mostra resumo do agendamento)
                </div>
                {data.buttons && data.buttons.map((btn, index) => (
                    <div key={index} style={{
                        background: "#eee",
                        padding: "5px",
                        margin: "5px 0",
                        borderRadius: "3px",
                        position: "relative",
                        textAlign: "left"
                    }}>
                        {btn.label}
                        <Handle
                            type="source"
                            position="right"
                            id={btn.id}
                            style={{ top: "50%", background: "#555" }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
});

export const VideoNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#fd7e14" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#fd7e14" }}>Video</div>
            <div className={classes.content}>
                {data.url ? (
                    <video src={data.url} style={{ maxWidth: "100%", maxHeight: "100px" }} controls />
                ) : (
                    <div>No Video Selected</div>
                )}
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const PixPaymentNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#4CAF50", minWidth: "200px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#4CAF50" }}>💰 Pagamento PIX</div>
            <div className={classes.content}>
                <div style={{ marginBottom: 5 }}>{data.title || "Solicitação de Pagamento"}</div>
                <div style={{ fontWeight: "bold", color: "#4CAF50", fontSize: "14px" }}>
                    R$ {(data.amount || 0).toFixed(2)}
                </div>
                {data.pixType && (
                    <div style={{ fontSize: "10px", marginTop: 5, color: "#888" }}>
                        {data.pixType === 'EVP' ? 'Chave Privada' : data.pixType}
                    </div>
                )}
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const TriggerConditionNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#ff9800", minWidth: "200px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#ff9800" }}>
                🔍 Condição por Gatilho
            </div>
            <div className={classes.content}>
                <div style={{ marginBottom: 10 }}>{data.label || "Verificar mensagem"}</div>
                <div style={{ fontSize: "10px", color: "#888", marginBottom: 10 }}>
                    {data.triggers?.length || 0} gatilho(s) configurado(s)
                </div>

                {/* Saída SIM (Verde) */}
                <div style={{
                    background: "#4CAF50",
                    color: "#fff",
                    padding: "5px",
                    margin: "5px 0",
                    borderRadius: "3px",
                    position: "relative",
                    textAlign: "left"
                }}>
                    ✓ SIM (Match)
                    <Handle
                        type="source"
                        position="right"
                        id="yes"
                        style={{ top: "50%", background: "#4CAF50" }}
                    />
                </div>

                {/* Saída NÃO (Vermelho) */}
                <div style={{
                    background: "#f44336",
                    color: "#fff",
                    padding: "5px",
                    margin: "5px 0",
                    borderRadius: "3px",
                    position: "relative",
                    textAlign: "left"
                }}>
                    ✗ NÃO (Sem match)
                    <Handle
                        type="source"
                        position="right"
                        id="no"
                        style={{ top: "50%", background: "#f44336" }}
                    />
                </div>
            </div>
        </div>
    );
});

export const TagBlockNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#9c27b0", minWidth: "200px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#9c27b0" }}>
                🏷️ Adicionar Tag
            </div>
            <div className={classes.content}>
                <div style={{ marginBottom: 5 }}>{data.label || "Selecionar Tags"}</div>
                <div style={{ fontSize: "10px", color: "#888", marginBottom: 5 }}>
                    {data.tags?.length || 0} tag(s) selecionada(s)
                </div>
                {data.tags && data.tags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                        {data.tags.slice(0, 3).map((tag, idx) => (
                            <span key={idx} style={{
                                background: tag.color || "#eee",
                                color: "#000",
                                fontSize: "9px",
                                padding: "2px 4px",
                                borderRadius: "4px"
                            }}>
                                {tag.name}
                            </span>
                        ))}
                        {data.tags.length > 3 && (
                            <span style={{ fontSize: "9px", color: "#888" }}>+{data.tags.length - 3}</span>
                        )}
                    </div>
                )}
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const ChatGPTNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#10a37f", minWidth: "220px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#10a37f" }}>
                🤖 ChatGPT
            </div>
            <div className={classes.content}>
                <div style={{ marginBottom: 5 }}>{data.systemPrompt ? data.systemPrompt.substring(0, 40) + "..." : "AI Assistant"}</div>
                <div style={{ fontSize: "10px", color: "#888", marginBottom: 5 }}>
                    Model: {data.model || "gpt-3.5-turbo"}
                </div>
                {data.intentDetection?.enabled && (
                    <div style={{ fontSize: "9px", color: "#10a37f", marginTop: 5 }}>
                        🎯 {data.intentDetection.intents?.length || 0} intenção(ões)
                    </div>
                )}

                {/* Main output */}
                <Handle type="source" position="bottom" style={{ background: "#10a37f" }} />

                {/* Intent outputs */}
                {data.intentDetection?.intents?.map((intent, index) => (
                    <Handle
                        key={intent.name}
                        type="source"
                        position="right"
                        id={intent.name}
                        style={{ top: 60 + (index * 25), background: "#10a37f" }}
                    />
                ))}
            </div>
        </div>
    );
});

export const ReturnToChatGPTNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#10a37f", minWidth: "200px", borderStyle: "dashed" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#10a37f" }}>
                🔙 Retornar ao ChatGPT
            </div>
            <div className={classes.content}>
                <div style={{ fontSize: "11px", marginBottom: 5 }}>
                    {data.message || "Pronto! Posso ajudar com mais alguma coisa?"}
                </div>
                <div style={{ fontSize: "9px", color: "#888", fontStyle: "italic" }}>
                    (Retorna ao node ChatGPT anterior)
                </div>
            </div>
            <Handle type="source" position="bottom" style={{ background: "#10a37f" }} />
        </div>
    );
});

export const PerplexityNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#20808d", minWidth: "220px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#20808d" }}>
                🔍 Perplexity
            </div>
            <div className={classes.content}>
                <div style={{ marginBottom: 5 }}>{data.systemPrompt ? data.systemPrompt.substring(0, 40) + "..." : "AI Assistant + Web Search"}</div>
                <div style={{ fontSize: "10px", color: "#888", marginBottom: 5 }}>
                    Model: {data.model || "sonar-small-online"}
                </div>
                {data.intentDetection?.enabled && (
                    <div style={{ fontSize: "9px", color: "#20808d", marginTop: 5 }}>
                        🎯 {data.intentDetection.intents?.length || 0} intenção(ões)
                    </div>
                )}

                {/* Main output */}
                <Handle type="source" position="bottom" style={{ background: "#20808d" }} />

                {/* Intent outputs */}
                {data.intentDetection?.intents?.map((intent, index) => (
                    <Handle
                        key={intent.name}
                        type="source"
                        position="right"
                        id={intent.name}
                        style={{ top: 60 + (index * 25), background: "#20808d" }}
                    />
                ))}
            </div>
        </div>
    );
});

export const ReturnToPerplexityNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#20808d", minWidth: "200px", borderStyle: "dashed" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#20808d" }}>
                🔙 Retornar ao Perplexity
            </div>
            <div className={classes.content}>
                <div style={{ fontSize: "11px", marginBottom: 5 }}>
                    {data.message || "Pronto! Posso ajudar com mais alguma coisa?"}
                </div>
                <div style={{ fontSize: "9px", color: "#888", fontStyle: "italic" }}>
                    (Retorna ao node Perplexity anterior)
                </div>
            </div>
            <Handle type="source" position="bottom" style={{ background: "#20808d" }} />
        </div>
    );
});

export const CarouselNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#ff6f00", minWidth: "250px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#ff6f00" }}>
                🎠 Carrossel
            </div>
            <div className={classes.content}>
                <div style={{ marginBottom: 10, fontSize: "11px" }}>
                    {data.label || "Clique para editar"}
                </div>
                <div style={{ fontSize: "10px", color: "#888", marginBottom: 10 }}>
                    {data.cards?.length || 0} card(s)
                </div>

                {/* Preview dos cards */}
                {data.cards && data.cards.slice(0, 2).map((card, cardIndex) => (
                    <div key={cardIndex} style={{
                        background: "#fff3e0",
                        padding: "8px",
                        margin: "5px 0",
                        borderRadius: "4px",
                        borderLeft: "3px solid #ff6f00",
                        textAlign: "left"
                    }}>
                        <div style={{ fontSize: "10px", fontWeight: "bold", marginBottom: 3 }}>
                            {card.title || `Card ${cardIndex + 1}`}
                        </div>
                        {card.image && (
                            <div style={{ fontSize: "9px", color: "#666", marginBottom: 3 }}>
                                🖼️ Imagem anexada
                            </div>
                        )}
                        {/* Botões do card com handles */}
                        {card.buttons && card.buttons.map((btn, btnIndex) => (
                            <div key={btnIndex} style={{
                                background: "#ff6f00",
                                color: "#fff",
                                padding: "3px 6px",
                                margin: "3px 0",
                                borderRadius: "3px",
                                fontSize: "9px",
                                position: "relative"
                            }}>
                                {btn.label || `Botão ${btnIndex + 1}`}
                                <Handle
                                    type="source"
                                    position="right"
                                    id={`card_${cardIndex}_btn_${btnIndex}`}
                                    style={{
                                        top: "50%",
                                        background: "#ff6f00",
                                        width: "8px",
                                        height: "8px"
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                ))}

                {data.cards && data.cards.length > 2 && (
                    <div style={{ fontSize: "9px", color: "#888", fontStyle: "italic", marginTop: 5 }}>
                        +{data.cards.length - 2} card(s) a mais
                    </div>
                )}
            </div>
        </div>
    );
});

export const StartOrderNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#4caf50", minWidth: "180px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#4caf50", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                <ShoppingCartIcon style={{ fontSize: 18 }} />
                Iniciar Pedido
            </div>
            <div className={classes.content}>
                {data.label || "Inicia a captura do pedido"}
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const PriceNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#ffc107", minWidth: "150px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#ffc107", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                <AttachMoneyIcon style={{ fontSize: 18 }} />
                Preço
            </div>
            <div className={classes.content}>
                <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                    R$ {(parseFloat(data.value) || 0).toFixed(2)}
                </div>
                <div style={{ fontSize: "10px", color: "#888" }}>
                    (Invisível para o cliente)
                </div>
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const OrderAddressNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#03a9f4", minWidth: "180px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#03a9f4", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                <LocationOnIcon style={{ fontSize: 18 }} />
                Endereço do Pedido
            </div>
            <div className={classes.content}>
                {data.label || "Informe suas instruções para o endereço..."}
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const OrderSummaryNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#673ab7", minWidth: "200px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#673ab7", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                <ReceiptIcon style={{ fontSize: 18 }} />
                Resumo Compra
            </div>
            <div className={classes.content}>
                <div style={{ fontSize: "11px", marginBottom: 10 }}>{data.label || "Aqui está o resumo do seu pedido:"}</div>
                <div style={{ fontStyle: "italic", fontSize: "10px", color: "#888", marginBottom: 10 }}>
                    (Mostra resumo + Sim/Não)
                </div>
                {/* Saída SIM */}
                <div style={{ background: "#eee", padding: "5px", margin: "5px 0", borderRadius: "3px", position: "relative", textAlign: "left" }}>
                    Sim
                    <Handle type="source" position="right" id="yes" style={{ top: "50%", background: "#4caf50" }} />
                </div>
                {/* Saída NÃO */}
                <div style={{ background: "#eee", padding: "5px", margin: "5px 0", borderRadius: "3px", position: "relative", textAlign: "left" }}>
                    Não
                    <Handle type="source" position="right" id="no" style={{ top: "50%", background: "#f44336" }} />
                </div>
            </div>
        </div>
    );
});

export const JumpNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#607d8b", minWidth: "150px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#607d8b", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                <RedoIcon style={{ fontSize: 18 }} />
                Pular / Ir para
            </div>
            <div className={classes.content}>
                <div style={{ fontSize: "11px", color: "#888" }}>Pular para o card:</div>
                <div style={{ fontWeight: "bold" }}>{data.targetNodeName || "Não selecionado"}</div>
            </div>
        </div>
    );
});


export const ProductsNode = memo(({ data }) => {
    const classes = useStyles();
    const selectedCount = data.selectedProducts?.length || 0;

    return (
        <div className={classes.node} style={{ borderColor: "#4caf50", minWidth: "200px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#4caf50", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                <ShoppingCartIcon style={{ fontSize: 18 }} />
                Produtos
            </div>
            <div className={classes.content}>
                <div style={{ fontSize: "11px", color: "#888", marginBottom: 5 }}>
                    {data.label || "Escolha um produto"}
                </div>
                <div style={{ fontWeight: "bold", color: selectedCount > 0 ? "#4caf50" : "#999" }}>
                    {selectedCount > 0 ? `${selectedCount} produto(s) selecionado(s)` : "Nenhum produto selecionado"}
                </div>
                {data.products && data.products.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: "10px", color: "#666" }}>
                        {data.products.slice(0, 2).map(p => (
                            <div key={p.id}>• {p.name}</div>
                        ))}
                        {data.products.length > 2 && <div>• +{data.products.length - 2} mais...</div>}
                    </div>
                )}
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});



export const QueueSelectionNode = memo(({ data }) => {
    const classes = useStyles();
    return (
        <div className={classes.node} style={{ borderColor: "#795548", minWidth: "200px" }}>
            <Handle type="target" position="top" />
            {data.onDuplicate && (
                <Tooltip title="Clonar bloco">
                    <IconButton className={classes.cloneButton} onClick={data.onDuplicate} size="small">
                        <FileCopyIcon className={classes.cloneIcon} />
                    </IconButton>
                </Tooltip>
            )}
            <div className={classes.header} style={{ color: "#795548", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                <AccountTreeIcon style={{ fontSize: 18 }} />
                Seleção de Fila
            </div>
            <div className={classes.content}>
                <div style={{ marginBottom: 10 }}>{data.label || "Escolha uma fila:"}</div>
                <div style={{ fontSize: "10px", color: "#888", fontStyle: "italic" }}>
                    (Listará todas as filas disponíveis)
                </div>
            </div>
            <Handle type="source" position="bottom" />
        </div>
    );
});

export const ButtonEdge = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    markerEnd,
    data,
}) => {
    const classes = useStyles();
    const edgePath = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    });
    const [edgeCenterX, edgeCenterY] = getEdgeCenter({
        sourceX,
        sourceY,
        targetX,
        targetY,
    });

    const onEdgeClick = (evt, id) => {
        evt.stopPropagation();
        if (data && data.onDelete) {
            data.onDelete(id);
        }
    };

    return (
        <>
            <path
                id={id}
                style={style}
                className="react-flow__edge-path"
                d={edgePath}
                markerEnd={markerEnd}
            />
            <foreignObject
                width={20}
                height={20}
                x={edgeCenterX - 10}
                y={edgeCenterY - 10}
                requiredExtensions="http://www.w3.org/1999/xhtml"
            >
                <div className={classes.edgeButton} onClick={(event) => onEdgeClick(event, id)}>
                    <CloseIcon style={{ fontSize: "14px", color: "red" }} />
                </div>
            </foreignObject>
        </>
    );
};




